import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { fetchPublicacionesEncabezadoApi } from '../../../api/axios';
import { fetchSellersFilter } from '../../../actions/sellersActions';
import { getEstadoPublicacionFromId } from '../../../helper/publicacionHelper';
import { filtroMedioMes, filtroHoy } from '../../../utilities/fechasFiltro';
import BigSpinner from '../../General/BigSpinner';
import SafeReactPaginate from '../../General/SafeReactPaginate';
import '../../General/AdminListCard.css';
import './ABM/AuditoriaPrecioStock.css';

const PAGE_SIZE = 10;
const DEFAULT_FECHAS = filtroMedioMes();

const PRESET_DAYS = [
    { value: '7', label: '7 días' },
    { value: '14', label: '14 días' },
    { value: '30', label: '30 días' },
    { value: '60', label: '60 días' },
    { value: '90', label: '90 días' },
    { value: 'custom', label: 'Personalizado' }
];

const ESTADO_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'activo', label: 'Activo' },
    { value: 'pausado', label: 'Pausado' },
    { value: 'pendiente_activacion', label: 'Pendiente' },
    { value: 'en_revision', label: 'En revisión' },
    { value: 'eliminado', label: 'Eliminado' }
];

const ORDEN_FECHA_OPTIONS = [
    { value: 'desc', label: 'Más recientes primero' },
    { value: 'asc', label: 'Más antiguas primero' }
];

const buildInitialFilters = () => ({
    fechaPubDesde: DEFAULT_FECHAS.desde,
    fechaPubHasta: DEFAULT_FECHAS.hasta,
    idSeller: '',
    idPublicacion: '',
    estado: '',
    presetDias: '14',
    ordenFechaCreacion: 'desc'
});

const mapOrdenToDireccion = (orden) => (orden === 'asc' ? 0 : 1);

const getSellerDescription = (item) => (
    item?.descripcionSeller
    ?? item?.DescripcionSeller
    ?? item?.nombreSeller
    ?? item?.NombreSeller
    ?? item?.seller?.descripcion
    ?? item?.Seller?.Descripcion
    ?? '-'
);

const normalizeElementos = (publicaciones) => {
    const elementos = publicaciones?.elementos ?? publicaciones?.content ?? publicaciones?.Content ?? [];
    return (Array.isArray(elementos) ? elementos : [])
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
            idPublicacion: item.idPublicacion ?? item.IdPublicacion,
            idSeller: item.idSeller ?? item.IdSeller,
            titulo: item.titulo ?? item.Titulo ?? '-',
            skuMarket: item.skuMarket ?? item.SkuMarket ?? '-',
            skuSeller: item.skuSeller ?? item.SkuSeller ?? '-',
            fechaPublicacion: item.fechaPublicacion ?? item.FechaPublicacion ?? null,
            fechaUltimaModificacion: item.fechaUltimaModificacion ?? item.FechaUltimaModificacion ?? null,
            estado: item.estado ?? item.Estado,
            descripcionSeller: getSellerDescription(item)
        }));
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short',
        timeStyle: 'short'
    }).format(date);
};

const formatEstadoLabel = (estado) => {
    if (estado == null || estado === '') return '-';
    const key = typeof estado === 'number'
        ? getEstadoPublicacionFromId(estado)
        : String(estado).toLowerCase();

    switch (key) {
        case 'activo':
            return 'Activo';
        case 'pausado':
            return 'Pausado';
        case 'pendiente_activacion':
            return 'Pendiente';
        case 'en_revision':
            return 'En revisión';
        case 'eliminado':
            return 'Eliminado';
        default:
            return String(estado);
    }
};

const getEstadoPillClass = (estado) => {
    const key = typeof estado === 'number'
        ? getEstadoPublicacionFromId(estado)
        : String(estado ?? '').toLowerCase();

    if (key === 'activo') return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--alta';
    if (key === 'eliminado') return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--baja';
    if (key === 'en_revision' || key === 'pendiente_activacion') {
        return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--edicion';
    }
    return 'auditoria-publicacion__accion-pill';
};

const computePresetRange = (days) => {
    const hasta = filtroHoy();
    const desdeDate = new Date();
    desdeDate.setDate(desdeDate.getDate() - Number(days));
    return {
        fechaPubDesde: desdeDate.toISOString().slice(0, 10),
        fechaPubHasta: hasta
    };
};

const buildRequestParams = ({ filters, page }) => {
    const idPublicacionValue = String(filters.idPublicacion ?? '').trim();
    const ordenFechaCreacion = filters.ordenFechaCreacion === 'asc' ? 'asc' : 'desc';

    const params = {
        pagina: page,
        idSeller: filters.idSeller || null,
        estado: filters.estado || null,
        entidadOrdenamiento: 'fechaPublicacion',
        direccionOrdenamiento: mapOrdenToDireccion(ordenFechaCreacion),
        DireccionOrdenamiento: mapOrdenToDireccion(ordenFechaCreacion),
        EntidadOrdenamiento: 'fechaPublicacion'
    };

    if (idPublicacionValue) {
        params.idPublicacion = idPublicacionValue;
    } else {
        params.fechaPubDesde = filters.fechaPubDesde || null;
        params.fechaPubHasta = filters.fechaPubHasta || null;
    }

    return params;
};

function FechasCreacionPublicaciones({ userType, sellers, fetchSellersFilter }) {
    const navigate = useNavigate();
    const isAdmin = userType?.isCategory || userType?.isAdminMod;

    const [filters, setFilters] = useState(buildInitialFilters);
    const [appliedFilters, setAppliedFilters] = useState(buildInitialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState({
        elementos: [],
        cantidadTotal: 0
    });

    useEffect(() => {
        if (!isAdmin) {
            navigate('/Publicaciones', { replace: true });
        }
    }, [isAdmin, navigate]);

    useEffect(() => {
        if (!isAdmin) return;
        fetchSellersFilter({ Descripcion: '', Page: 1, PageSize: 100 });
    }, [fetchSellersFilter, isAdmin]);

    const fetchData = useCallback(async (nextFilters, page) => {
        setLoading(true);
        setError('');

        try {
            const raw = await fetchPublicacionesEncabezadoApi(buildRequestParams({ filters: nextFilters, page }));
            setResult({
                elementos: normalizeElementos(raw),
                cantidadTotal: raw?.cantidadTotal ?? raw?.CantidadTotal ?? raw?.total ?? raw?.Total ?? 0
            });
        } catch (err) {
            setResult({ elementos: [], cantidadTotal: 0 });
            setError(err?.response?.data?.mensaje || 'No se pudieron cargar las fechas de creación.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        fetchData(appliedFilters, currentPage);
    }, [appliedFilters, currentPage, fetchData, isAdmin]);

    const pageCount = Math.max(0, Math.ceil((result.cantidadTotal || 0) / PAGE_SIZE));
    const safeForcePage = Math.min(Math.max(currentPage - 1, 0), Math.max(pageCount - 1, 0));

    const ordenFechaLabel = ORDEN_FECHA_OPTIONS.find(
        (option) => option.value === (appliedFilters.ordenFechaCreacion ?? 'desc')
    )?.label ?? 'Más recientes primero';

    const summaryCards = useMemo(() => ([
        { label: 'Total en consulta', value: result.cantidadTotal || 0 },
        { label: 'En esta página', value: result.elementos.length },
        {
            label: 'Período',
            value: appliedFilters.fechaPubDesde && appliedFilters.fechaPubHasta
                ? `${appliedFilters.fechaPubDesde} — ${appliedFilters.fechaPubHasta}`
                : 'Sin filtro de fechas'
        },
        { label: 'Orden por creación', value: ordenFechaLabel }
    ]), [
        appliedFilters.fechaPubDesde,
        appliedFilters.fechaPubHasta,
        appliedFilters.ordenFechaCreacion,
        ordenFechaLabel,
        result.cantidadTotal,
        result.elementos.length
    ]);

    const sellerOptions = useMemo(() => {
        const options = (sellers || []).map((item) => ({
            value: String(item.value ?? item.idSeller ?? item.IdSeller ?? ''),
            label: item.label ?? item.descripcion ?? item.Descripcion ?? `Seller ${item.value}`
        })).filter((item) => item.value);

        return [{ value: '', label: 'Todos' }, ...options];
    }, [sellers]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;

        if (name === 'presetDias' && value !== 'custom') {
            const range = computePresetRange(value);
            setFilters((previous) => ({
                ...previous,
                presetDias: value,
                ...range
            }));
            return;
        }

        setFilters((previous) => ({
            ...previous,
            [name]: value,
            ...(name === 'presetDias' ? {} : { presetDias: 'custom' })
        }));
    };

    const applyOrdenFechaCreacion = (ordenFechaCreacion) => {
        const normalizedOrden = ordenFechaCreacion === 'asc' ? 'asc' : 'desc';
        setFilters((previous) => ({ ...previous, ordenFechaCreacion: normalizedOrden }));
        setAppliedFilters((previous) => ({ ...previous, ordenFechaCreacion: normalizedOrden }));
        setCurrentPage(1);
    };

    const handleApplyFilters = (event) => {
        event.preventDefault();
        setCurrentPage(1);
        setAppliedFilters(filters);
    };

    const handleOrdenFechaChange = (event) => {
        applyOrdenFechaCreacion(event.target.value);
    };

    const toggleOrdenFechaCreacion = () => {
        const nextOrden = appliedFilters.ordenFechaCreacion === 'asc' ? 'desc' : 'asc';
        applyOrdenFechaCreacion(nextOrden);
    };

    const handleResetFilters = (event) => {
        event.preventDefault();
        const nextFilters = buildInitialFilters();
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
    };

    const handlePageClick = (data) => {
        setCurrentPage(data.selected + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <div className="container py-3 auditoria-modulo">
            <div className="row mb-3 mb-md-4 align-items-center py-3 px-2 rounded admin-page-heading auditoria-page-heading">
                <div className="col">
                    <h4 className="mb-0 text-themecolor color-rojo-megatone fw-bold">
                        Fechas de creación de publicaciones
                    </h4>
                    <p className="small text-body-secondary mb-0 mt-1">
                        Consulta administrativa de cuándo se crearon las publicaciones y su última modificación.
                    </p>
                </div>
            </div>

            <div className="auditoria-publicacion">
                <form onSubmit={handleApplyFilters} onReset={handleResetFilters}>
                    <div className="auditoria-publicacion__filters-card">
                        <div className="auditoria-publicacion__filters-header">
                            <h6 className="auditoria-publicacion__filters-title">
                                <i className="fa fa-filter" aria-hidden="true" />
                                Filtros de búsqueda
                            </h6>
                        </div>
                        <div className="auditoria-publicacion__filters-body">
                            <div className="row g-3 auditoria-publicacion__toolbar-grid">
                                <div className="col-12 col-md-3">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Período rápido</p>
                                    <select
                                        className="form-control"
                                        name="presetDias"
                                        value={filters.presetDias}
                                        onChange={handleInputChange}
                                    >
                                        {PRESET_DAYS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12 col-md-3">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Creación desde</p>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="fechaPubDesde"
                                        value={filters.fechaPubDesde || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-12 col-md-3">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Creación hasta</p>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="fechaPubHasta"
                                        value={filters.fechaPubHasta || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-12 col-md-3">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Seller</p>
                                    <select
                                        className="form-control"
                                        name="idSeller"
                                        value={filters.idSeller}
                                        onChange={handleInputChange}
                                    >
                                        {sellerOptions.map((option) => (
                                            <option key={option.value || 'all'} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12 col-md-3">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Estado</p>
                                    <select
                                        className="form-control"
                                        name="estado"
                                        value={filters.estado}
                                        onChange={handleInputChange}
                                    >
                                        {ESTADO_OPTIONS.map((option) => (
                                            <option key={option.value || 'all'} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12 col-md-3">
                                    <p className="auditoria-publicacion__filtro-label mb-0">ID publicación</p>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="idPublicacion"
                                        value={filters.idPublicacion}
                                        onChange={handleInputChange}
                                        placeholder="Ej: 123456"
                                    />
                                </div>
                                <div className="col-12 col-md-3">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Orden por fecha creación</p>
                                    <select
                                        className="form-control"
                                        name="ordenFechaCreacion"
                                        value={filters.ordenFechaCreacion ?? 'desc'}
                                        onChange={handleOrdenFechaChange}
                                    >
                                        {ORDEN_FECHA_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="auditoria-publicacion__toolbar-actions">
                                <button
                                    type="submit"
                                    className="auditoria-publicacion__filter-btn auditoria-publicacion__filter-btn--primary"
                                    disabled={loading}
                                >
                                    {loading ? 'Buscando...' : 'Buscar'}
                                </button>
                                <button type="reset" className="auditoria-publicacion__filter-btn">
                                    Limpiar
                                </button>
                            </div>
                        </div>
                    </div>
                </form>

                <div className="auditoria-publicacion__metrics">
                    {summaryCards.map((card) => (
                        <div key={card.label} className="auditoria-publicacion__card">
                            <strong>{card.value}</strong>
                            <span className="auditoria-publicacion__label">{card.label}</span>
                        </div>
                    ))}
                </div>

                {error ? (
                    <div className="auditoria-publicacion__state auditoria-publicacion__state--error" role="alert">
                        {error}
                    </div>
                ) : null}

                {loading ? (
                    <BigSpinner message="Cargando fechas de creación..." />
                ) : !error && result.elementos.length === 0 ? (
                    <div className="auditoria-publicacion__state">
                        No se encontraron publicaciones para los filtros seleccionados.
                    </div>
                ) : !error ? (
                    <>
                        <div className="auditoria-publicacion__results-card">
                            <div className="auditoria-publicacion__results-header">
                                <h5 className="auditoria-publicacion__results-title mb-0">Publicaciones</h5>
                                <span className="auditoria-publicacion__results-badge">
                                    {result.elementos.length} en esta página
                                </span>
                            </div>
                            <div className="auditoria-publicacion__results-body">
                                <div className="table-responsive">
                                    <table className="table table-hover auditoria-publicacion__table mb-0">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Seller</th>
                                                <th>Título</th>
                                                <th>SKU Market</th>
                                                <th>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-decoration-none text-reset fw-semibold border-0"
                                                        onClick={toggleOrdenFechaCreacion}
                                                        title="Cambiar orden de fecha de creación"
                                                    >
                                                        Fecha creación
                                                        {' '}
                                                        <i
                                                            className={`fa ${
                                                                appliedFilters.ordenFechaCreacion === 'asc'
                                                                    ? 'fa-sort-amount-asc'
                                                                    : 'fa-sort-amount-desc'
                                                            }`}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                </th>
                                                <th>Última modificación</th>
                                                <th>Estado</th>
                                                <th>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.elementos.map((item) => (
                                                <tr key={item.idPublicacion}>
                                                    <td><strong>{item.idPublicacion}</strong></td>
                                                    <td>{item.descripcionSeller}</td>
                                                    <td>{item.titulo}</td>
                                                    <td>{item.skuMarket}</td>
                                                    <td>{formatDateTime(item.fechaPublicacion)}</td>
                                                    <td>{formatDateTime(item.fechaUltimaModificacion)}</td>
                                                    <td>
                                                        <span className={getEstadoPillClass(item.estado)}>
                                                            {formatEstadoLabel(item.estado)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Link
                                                            to={`/Publicaciones/Editar/${item.idSeller}/${item.idPublicacion}`}
                                                            className="btn btn-sm btn-outline-secondary"
                                                        >
                                                            Ver
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {pageCount > 0 && (
                            <div className="d-flex justify-content-center mt-3">
                                <SafeReactPaginate
                                    previousLabel="Anterior"
                                    nextLabel="Siguiente"
                                    breakLabel="..."
                                    breakClassName="break-me"
                                    pageCount={pageCount}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={handlePageClick}
                                    containerClassName="pagination"
                                    subContainerClassName="pages pagination"
                                    activeClassName="active"
                                    forcePage={safeForcePage}
                                />
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    userType: state.general.userType,
    sellers: state.sellers.filteredSellers
});

export default connect(mapStateToProps, { fetchSellersFilter })(FechasCreacionPublicaciones);
